import { __assign } from "tslib";
import { Plugin } from 'prosemirror-state';
import { TableMap } from 'prosemirror-tables';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { colgroupAttr } from '../../config/constants';
import { TableView, TableWrapperView } from './table-view';
import { parseStyle, setNodeStyle } from './../../utils';
import { cellIndexes, domCellAround, otherResizeHandle, otherResizing, edgeCell, tableColumnResizeKey as key } from './utils';
export function columnResizing() {
    var handleWidth = 5, cellMinWidth = 25;
    var plugin = new Plugin({
        key: key,
        state: {
            init: function () {
                return new ResizeState(-1, null);
            },
            apply: function (tr, prev) {
                return prev.apply(tr);
            }
        },
        props: {
            attributes: function (state) {
                if (otherResizeHandle(key, state)) {
                    return null;
                }
                var pluginState = key.getState(state);
                return pluginState.activeHandle > -1 ? { class: 'resize-cursor' } : null;
            },
            handleDOMEvents: {
                mousemove: function (view, event) {
                    if (!otherResizing(key, view.state)) {
                        handleMouseMove(view, event, handleWidth);
                    }
                    return false;
                },
                mouseleave: function (view) {
                    handleMouseLeave(view);
                    return false;
                },
                mousedown: function (view, event) {
                    return handleMouseDown(view, event, cellMinWidth);
                }
            },
            decorations: function (state) {
                if (!otherResizing(key, state)) {
                    var pluginState = key.getState(state);
                    if (pluginState.activeHandle > -1) {
                        return handleDecorations(state, pluginState.activeHandle);
                    }
                }
                return DecorationSet.empty;
            },
            nodeViews: {
                table_wrapper: function (node, view) { return new TableWrapperView(node, view); },
                table: function (node, view) { return new TableView(node, view); }
            }
        }
    });
    return plugin;
}
function pointsAtCell($pos) {
    return Boolean($pos.parent.type.spec.tableRole === 'row' && $pos.nodeAfter);
}
var ResizeState = /** @class */ (function () {
    function ResizeState(activeHandle, dragging) {
        this.activeHandle = activeHandle;
        this.dragging = dragging;
    }
    ResizeState.prototype.apply = function (tr) {
        var state;
        var action = tr.getMeta(key);
        if (action && action.setHandle != null) {
            return new ResizeState(action.setHandle, null);
        }
        if (action && action.setDragging !== undefined) {
            return new ResizeState(this.activeHandle, action.setDragging);
        }
        if (this.activeHandle > -1 && tr.docChanged) {
            var handle = tr.mapping.map(this.activeHandle, -1);
            if (!pointsAtCell(tr.doc.resolve(handle))) {
                handle = -1;
            }
            state = new ResizeState(handle, this.dragging);
        }
        return state || this;
    };
    return ResizeState;
}());
function handleMouseMove(view, event, handleWidth) {
    var pluginState = key.getState(view.state);
    if (!pluginState.dragging) {
        var target = domCellAround(event.target);
        var cell = -1;
        if (target) {
            var indexes = cellIndexes(target);
            var _a = target.getBoundingClientRect(), left = _a.left, right = _a.right;
            if (Math.abs(event.clientX - left) <= handleWidth && indexes.cellIndex > 0) {
                indexes.cellIndex -= target.colSpan;
                cell = edgeCell(view, event, indexes);
            }
            else if (right - event.clientX > 0 && right - event.clientX <= handleWidth) {
                cell = edgeCell(view, event, indexes);
            }
        }
        if (cell !== pluginState.activeHandle) {
            updateHandle(view, cell);
        }
    }
}
function handleMouseLeave(view) {
    var pluginState = key.getState(view.state);
    if (pluginState.activeHandle > -1 && !pluginState.dragging) {
        updateHandle(view, -1);
    }
}
function handleMouseDown(view, event, cellMinWidth) {
    var _a;
    var pluginState = key.getState(view.state);
    if (pluginState.activeHandle === -1 || pluginState.dragging) {
        return false;
    }
    var $cell = view.state.doc.resolve(pluginState.activeHandle);
    var row = $cell.parent;
    var cellIndex = $cell.index();
    var colSpan = 0;
    for (var i = 0; i <= cellIndex; i++) {
        colSpan += row.child(i).attrs.colspan;
    }
    var tableNode = $cell.node($cell.depth - 1);
    var dom = view.domAtPos(pluginState.activeHandle);
    var domCell = dom.node.childNodes[dom.offset];
    var tableDom = domCell.closest('table');
    var col, tableAttrs;
    if (tableNode.attrs[colgroupAttr]) {
        var colgroup = tableDom.firstChild;
        col = colgroup.children[colSpan - 1];
        if (!col.style.width) {
            col.style.width = col.offsetWidth + 'px';
        }
    }
    else {
        var total = 0;
        for (var i = 0; i < row.childCount; i++) {
            total += row.child(i).attrs.colspan;
        }
        var colgroup = document.createElement('colgroup');
        var cols = new Array(total);
        for (var i = 0; i < total; i++) {
            cols[i] = document.createElement('col');
            colgroup.appendChild(cols[i]);
        }
        tableDom.insertBefore(colgroup, tableDom.firstChild);
        col = cols[cellIndex];
        col.style.width = col.offsetWidth + 'px';
        tableAttrs = __assign(__assign({}, tableNode.attrs), (_a = {}, _a[colgroupAttr] = '<colgroup>' + cols.reduce(function (acc, cur) { return acc + cur.outerHTML; }, '') + '</colgroup>', _a));
    }
    var width = parseFloat(col.style.width);
    var tr = view.state.tr.setMeta(key, { setDragging: { startX: event.clientX, startWidth: width } });
    if (!tableDom.style.width) {
        var widths = Array.from(col.parentNode.children).map(function (c) { return c.style.width; });
        if (widths.every(Boolean)) {
            var sum = widths.reduce(function (acc, cur) { return acc + parseFloat(cur); }, 0);
            tableAttrs = setNodeStyle(tableAttrs || tableNode.attrs, 'width', sum + 'px');
        }
    }
    if (tableAttrs) {
        var tablePos = $cell.posAtIndex(0, $cell.depth - 1) - 1;
        tr.setNodeMarkup(tablePos, null, tableAttrs);
    }
    view.dispatch(tr);
    function finish(ev) {
        ev.view.removeEventListener('mouseup', finish);
        ev.view.removeEventListener('mousemove', move);
        var curPluginState = key.getState(view.state);
        if (curPluginState.dragging) {
            var transaction = view.state.tr;
            updateColumnWidth(view, transaction, curPluginState.activeHandle);
            view.dispatch(transaction.setMeta(key, { setDragging: null }));
        }
    }
    function move(ev) {
        if (!ev.which) {
            return finish(ev);
        }
        var curPluginState = key.getState(view.state);
        var dragged = draggedWidth(curPluginState.dragging, ev, cellMinWidth);
        displayColumnWidth(view, curPluginState.activeHandle, dragged);
    }
    event.view.addEventListener('mouseup', finish);
    event.view.addEventListener('mousemove', move);
    event.preventDefault();
    return true;
}
function draggedWidth(dragging, event, cellMinWidth) {
    var offset = event.clientX - dragging.startX;
    return Math.max(cellMinWidth, dragging.startWidth + offset);
}
function updateHandle(view, value) {
    var tr = view.state.tr;
    tr.setMeta('addToHistory', false);
    tr.setMeta(key, { setHandle: value });
    view.dispatch(tr);
}
function updateColumnWidth(view, tr, cell) {
    var _a;
    var $cell = view.state.doc.resolve(cell);
    var tableNode = $cell.node(-1), start = $cell.start(-1);
    var tablePos = $cell.posAtIndex(0, $cell.depth - 1) - 1;
    var tableDom = view.nodeDOM(start).closest('table');
    var attrs = tableNode.attrs;
    if (tableNode && attrs[colgroupAttr]) {
        var colgroup = tableDom.firstChild;
        attrs = __assign(__assign({}, attrs), (_a = {}, _a[colgroupAttr] = colgroup.outerHTML, _a));
    }
    var tableDomWidth = tableDom.style.width;
    if (tableDom && tableDomWidth && parseStyle(attrs.style).width !== tableDomWidth) {
        attrs = setNodeStyle(attrs, 'width', tableDomWidth);
    }
    tr.setNodeMarkup(tablePos, null, attrs);
}
function displayColumnWidth(view, cell, width) {
    var $cell = view.state.doc.resolve(cell);
    var table = $cell.node(-1), start = $cell.start(-1);
    var col = TableMap.get(table).colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan - 1;
    var dom = view.domAtPos($cell.start(-1)).node;
    if (dom.nodeName !== 'TABLE') {
        dom = dom.closest('table');
    }
    var tableDom = dom;
    var colgroup = tableDom.firstChild;
    var cols = Array.from(colgroup.children);
    cols[col].style.width = width + 'px';
    if (tableDom.style.width) {
        var widths = cols.map(function (c) { return c.style.width; });
        if (widths.every(Boolean)) {
            var sum = widths.reduce(function (acc, cur) { return acc + parseFloat(cur); }, 0);
            tableDom.style.width = sum + 'px';
        }
    }
}
function handleDecorations(state, cell) {
    var decorations = [];
    var $cell = state.doc.resolve(cell);
    var table = $cell.node(-1), map = TableMap.get(table), start = $cell.start(-1);
    var cellsPositions = [];
    var cellPos = cell - start;
    var column = -1;
    for (var i = 0; i < map.height; i++) {
        cellsPositions.push(map.map.slice(i * map.width, (i * map.width) + map.width));
        if (column === -1) {
            column = cellsPositions[i].lastIndexOf(cellPos);
        }
    }
    var cells = [];
    cellsPositions.forEach(function (row) {
        var c = row[column];
        if (row[column] !== row[column + 1]) {
            cells.push(c);
        }
    });
    cells.forEach((function (cPos) {
        var pos = start + cPos + table.nodeAt(cPos).nodeSize - 1;
        var dom = document.createElement('div');
        dom.className = 'column-resize-handle';
        decorations.push(Decoration.widget(pos, dom));
    }));
    return DecorationSet.create(state.doc, decorations);
}
